import { arrayOf, oneOfType, string, number, bool, shape } from 'prop-types';

import { explicitNull, or } from '@common/tempAirbnbPropTypesReplacement';

export const cmsBaseBlockShape = shape({
  id: string.isRequired,
  type: string.isRequired,
  value: oneOfType([string, shape({})]).isRequired,
});

export const cmsPageImgShape = shape({
  url: string,
  title: string,
  altText: string,
  width: number,
  height: number,
});

export const cmsPageShape = shape({
  id: number.isRequired,
  meta: shape({
    metaTitle: string.isRequired,
    metaDescription: string.isRequired,
    metaImage: cmsPageImgShape,
    htmlUrl: string.isRequired,
    langUrls: shape({
      de: string,
      fr: string,
    }),
  }).isRequired,
  title: string.isRequired,
  image: or([explicitNull(), cmsPageImgShape]).isRequired,
  lead: or([explicitNull(), string]).isRequired,
  body: or([explicitNull(), arrayOf(cmsBaseBlockShape)]).isRequired,
});

export const menuItemShape = shape({
  id: number.isRequired,
  title: string.isRequired,
  linkUrl: string.isRequired,
});

export const menuItemsType = arrayOf(menuItemShape);

export const videoShape = shape({
  stream: string,
  fallback: string,
});

export const cmsComponentData = shape({
  componentId: string,
  results: arrayOf(shape({})),
  isError: bool,
});

export const cmsComponentsData = arrayOf(cmsComponentData);

export type CmsComponentValue = {
  componentId: string;
  results: Array<Record<string, unknown>>;
  isError: boolean;
};

export type CmsComponentData = {
  status: string;
  value: CmsComponentValue | null;
};

export type CmsPageMetaImage = {
  url: string;
  title: string;
  altText: string;
  width: number;
  height: number;
};

export type CmsBlock = {
  id: string;
  type: string;
  value: string | Record<string, unknown>;
};

export type CmsVideoBlock = {
  stream: string;
  fallback: string;
};

export type CmsPageMeta = {
  metaTitle: string;
  metaDescription: string;
  metaImage: CmsPageMetaImage;
  htmlUrl: string;
  langUrls: {
    de: string;
    fr: string;
  };
};

export type CmsPage = {
  id: number;
  meta: CmsPageMeta;
  title: string;
  image: CmsPageMetaImage;
  lead: string;
  body: Array<CmsBlock>;
};

export type CmsGenericPage = CmsPage & {
  video: string;
  videoDesktop: string;
  categoryTitle: string;
};

export type JobOfferDetailsPage = CmsPage & {
  apprenticeship: boolean;
  areaOfOperation: {
    body: Array<CmsBlock>;
    intro: Array<CmsBlock>;
    furtherSelection: boolean;
    id: number;
    jobOfferHeading: string;
    slug: string;
    title: string;
    lead: string;
  };
  body: string;
  description: string;
  heading: string;
  workloadFrom: number;
  workloadTo: number;
  store: {
    id: number;
    title: string;
    canton: string;
    street: string;
    houseNr: string;
    zipCode: string;
    city: string;
  };
  placeNameAddition: string;
  image: string;
};

export type JobAreaOfOperation = CmsPage & {
  body: string | Array<CmsBlock>;
  bodySecond: string | Array<CmsBlock>;
  heading: string;
  id: number;
  image: string;
  intro: string | Array<CmsBlock>;
  lead: string;
  metaDescription: string;
  slug: string;
  title: string;
  titleTag: string;
  video: CmsVideoBlock;
  videoDesktop: CmsVideoBlock;
  subtitleOne: string;
  subtitleTwo: string;
  apprenticeship: boolean;
  showButton: boolean;
};

export type LookbookPageData = CmsPage & {
  body: Array<CmsBlock>;
  title: string;
  subtitle: string;
  image: string;
  lead: string;
  video: string;
  videoDesktop: string;
  productsExpiryDate: string;
};
